import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiChevronDown, FiCircle, FiSkipForward, FiUploadCloud } from "react-icons/fi";
import {
  NegotiationRoundStatus,
  NegotiationRoundV2Dto,
  SkippableGateDto,
  SourcingEventStatus,
  SourcingEventV2Dto,
  useForcePublishEventMutation,
  useSkipPastGateMutation,
} from "../../../autogen/bff-api";
import { requiresStandstill } from "./award/requiresStandstill";
import { useApiError } from "../../../common/errors/useApiError";
import { requireBooleanEnvVar } from "../../../config";
import { isTwoStageProcedure } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

interface Props {
  event: SourcingEventV2Dto;
  onForcePublished?: () => void;
}

export type DevFastForwardAction = "FORCE_PUBLISH" | SkippableGateDto;

export const devFastForwardActions = (
  event: SourcingEventV2Dto,
  now: Date = new Date(),
  rounds: NegotiationRoundV2Dto[] = [],
): DevFastForwardAction[] => {
  const isAhead = (iso: string | null | undefined) => !!iso && new Date(iso).getTime() > now.getTime();
  const actions: DevFastForwardAction[] = [];
  const publicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const round = publicCtx?.qualificationRound;
  if (event.status === SourcingEventStatus.Draft || event.status === SourcingEventStatus.PublicationPending) {
    actions.push("FORCE_PUBLISH");
  }
  if (round && !round.invitation && isAhead(round.requestDeadline)) {
    actions.push(SkippableGateDto.RequestDeadline);
  }
  if (isAhead(publicCtx?.dpsScheme?.validFrom)) {
    actions.push(SkippableGateDto.DpsSchemeStart);
  }
  // Server-resolved deadline: null for two-stage events until the invitation confirms one.
  if (event.status === SourcingEventStatus.Published && isAhead(event.milestoneDates.bidSubmissionDeadline)) {
    actions.push(SkippableGateDto.BidSubmissionDeadline);
  }
  // An open round holds the competition past the tilbudsfrist and cannot be completed before its
  // own frist, so it is the next thing a demo has to wait out.
  if (openRoundDeadline(rounds, isAhead)) {
    actions.push(SkippableGateDto.NegotiationRoundDeadline);
  }
  if (event.status === SourcingEventStatus.AwardDecision && isAhead(event.award?.standstillExpiresAt)) {
    actions.push(SkippableGateDto.Standstill);
  }
  return actions;
};

const openRoundDeadline = (
  rounds: NegotiationRoundV2Dto[],
  isAhead: (iso: string | null | undefined) => boolean,
): boolean => {
  const open = rounds
    .filter((r) => r.status === NegotiationRoundStatus.Published)
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1);
  return !!open && isAhead(open.deadline);
};

export type RoadmapStepState = "done" | "next" | "blocked";

export type DevFastForwardReasonKey =
  | "requires publishing first"
  | "requires invitation to tender first"
  | "requires a published negotiation round first"
  | "requires award and dispatched letters first";

export interface RoadmapStep {
  key: DevFastForwardAction;
  state: RoadmapStepState;
  // i18n key for why a blocked step isn't reachable yet (the manual action required first).
  reasonKey?: DevFastForwardReasonKey;
}

const PAST_REQUEST_PHASE: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
];
const PAST_BID_PHASE: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
];

export const devFastForwardRoadmap = (
  event: SourcingEventV2Dto,
  now: Date = new Date(),
  rounds: NegotiationRoundV2Dto[] = [],
): { steps: RoadmapStep[]; next: DevFastForwardAction | null } => {
  const next = devFastForwardActions(event, now, rounds)[0] ?? null;
  const roadmapPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const twoStage = isTwoStageProcedure(roadmapPublicCtx?.procedureType);
  const published = event.status !== SourcingEventStatus.Draft && event.status !== SourcingEventStatus.PublicationPending;
  const invited = roadmapPublicCtx?.qualificationRound?.invitation != null;
  const requestPhaseDone = invited || PAST_REQUEST_PHASE.includes(event.status);
  const bidPhaseDone = PAST_BID_PHASE.includes(event.status);
  const standstillDone = event.status === SourcingEventStatus.Awarded || event.status === SourcingEventStatus.Closed;

  const roundsDone = rounds.length > 0 && rounds.every((r) => r.status !== NegotiationRoundStatus.Published);
  const scheme = roadmapPublicCtx?.dpsScheme ?? null;
  const schemeStarted = scheme != null && new Date(scheme.validFrom) <= now;
  const order: DevFastForwardAction[] = [
    "FORCE_PUBLISH",
    ...(twoStage ? [SkippableGateDto.RequestDeadline] : []),
    ...(scheme ? [SkippableGateDto.DpsSchemeStart] : []),
    // A system collects no tenders of its own; each purchase under it runs its own roadmap.
    ...(scheme ? [] : [SkippableGateDto.BidSubmissionDeadline]),
    // Only a competition that actually has rounds shows the round step; the roadmap describes the
    // path this event is on, not every path the platform supports.
    ...(rounds.length > 0 ? [SkippableGateDto.NegotiationRoundDeadline] : []),
    ...(requiresStandstill(event) ? [SkippableGateDto.Standstill] : []),
  ];

  const steps = order.map<RoadmapStep>((key) => {
    if (key === next) return { key, state: "next" };
    switch (key) {
      case "FORCE_PUBLISH":
        return { key, state: published ? "done" : "blocked", reasonKey: undefined };
      case SkippableGateDto.RequestDeadline:
        return requestPhaseDone
          ? { key, state: "done" }
          : { key, state: "blocked", reasonKey: "requires publishing first" };
      case SkippableGateDto.DpsSchemeStart:
        return schemeStarted
          ? { key, state: "done" }
          : { key, state: "blocked", reasonKey: "requires publishing first" };
      case SkippableGateDto.BidSubmissionDeadline:
        return bidPhaseDone
          ? { key, state: "done" }
          : {
              key,
              state: "blocked",
              reasonKey: twoStage ? "requires invitation to tender first" : "requires publishing first",
            };
      case SkippableGateDto.NegotiationRoundDeadline:
        return roundsDone
          ? { key, state: "done" }
          : { key, state: "blocked", reasonKey: "requires a published negotiation round first" };
      case SkippableGateDto.Standstill:
        return standstillDone
          ? { key, state: "done" }
          : { key, state: "blocked", reasonKey: "requires award and dispatched letters first" };
      default:
        return { key, state: "blocked" };
    }
  });

  return { steps, next };
};

export const DevFastForwardMenu = (props: Props) => {
  if (!requireBooleanEnvVar("VITE_ENABLE_DEV_TOOLS")) return null;
  return <FastForwardButton {...props} />;
};

// t('Force publish') t('Skip past request deadline') t('Skip past bid deadline')
// t('Skip past negotiation round deadline') t('Skip past standstill')
// t('Skip to the start of the system')
// t('requires a published negotiation round first')
const ACTION_LABEL_KEYS: Record<DevFastForwardAction, string> = {
  FORCE_PUBLISH: "Force publish",
  [SkippableGateDto.RequestDeadline]: "Skip past request deadline",
  [SkippableGateDto.DpsSchemeStart]: "Skip to the start of the system",
  [SkippableGateDto.BidSubmissionDeadline]: "Skip past bid deadline",
  [SkippableGateDto.NegotiationRoundDeadline]: "Skip past negotiation round deadline",
  [SkippableGateDto.Standstill]: "Skip past standstill",
};

const ACTION_ICONS: Record<DevFastForwardAction, IconType> = {
  FORCE_PUBLISH: FiUploadCloud,
  [SkippableGateDto.RequestDeadline]: FiSkipForward,
  [SkippableGateDto.DpsSchemeStart]: FiSkipForward,
  [SkippableGateDto.BidSubmissionDeadline]: FiSkipForward,
  [SkippableGateDto.NegotiationRoundDeadline]: FiSkipForward,
  [SkippableGateDto.Standstill]: FiSkipForward,
};

// Deliberate raw-palette deviation from the semantic-token rule: dev-only chrome must not blend in.
const DevTag = () => (
  <Flex
    align="center"
    px="2"
    bg="purple.600"
    color="white"
    fontFamily="mono"
    fontSize="2xs"
    fontWeight="bold"
    letterSpacing="wider"
  >
    DEV
  </Flex>
);

const FastForwardButton = ({ event, onForcePublished }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { triggerSoft } = useApiError();
  const [skipPastGate, { isLoading: isSkipping }] = useSkipPastGateMutation();
  const [forcePublishEvent, { isLoading: isPublishing }] = useForcePublishEventMutation();
  const busy = isSkipping || isPublishing;
  const { rounds } = useRoundSelection();
  const { steps, next } = devFastForwardRoadmap(event, new Date(), rounds);

  const run = async (action: DevFastForwardAction) => {
    if (action === "FORCE_PUBLISH") {
      try {
        await forcePublishEvent({ eventId: event.id }).unwrap();
        toast({ title: t("Published"), status: "success", duration: 4000 });
        onForcePublished?.();
      } catch (e) {
        triggerSoft(e);
      }
      return;
    }
    try {
      const result = await skipPastGate({ eventId: event.id, skipPastGateRequestDto: { gate: action } }).unwrap();
      toast({
        title:
          result.skewSeconds > 0
            ? t("Skipped {{skew}}", { skew: formatSkew(result.skewSeconds) })
            : t("Deadline had already passed"),
        status: "success",
        duration: 4000,
      });
    } catch (e) {
      triggerSoft(e);
    }
  };

  // Nothing reachable: keep the button visible but disabled, so it never reads as "misconfigured".
  if (next === null) {
    return (
      <Tooltip label={t("No shortcut from here — you do the next step yourself")} placement="bottom-end">
        <Flex
          align="stretch"
          h="8"
          border="1px solid"
          borderColor="smBorder"
          borderRadius="md"
          overflow="hidden"
          opacity={0.6}
        >
          <Flex align="center" px="2" bg="gray.400" color="white" fontFamily="mono" fontSize="2xs" fontWeight="bold" letterSpacing="wider">
            DEV
          </Flex>
          <Flex align="center" px="3" fontSize="sm" fontStyle="italic" color="smMuted">
            {t("No shortcut")}
          </Flex>
        </Flex>
      </Tooltip>
    );
  }

  return (
    <Menu placement="bottom-end">
      <Flex
        align="stretch"
        h="8"
        border="1px solid"
        borderColor="purple.200"
        borderRadius="md"
        overflow="hidden"
        bg="white"
        opacity={busy ? 0.6 : 1}
        _hover={{ borderColor: "purple.300" }}
      >
        <DevTag />
        <Button
          variant="ghost"
          colorScheme="purple"
          size="sm"
          borderRadius="0"
          fontWeight="semibold"
          color="purple.700"
          rightIcon={<Icon as={ACTION_ICONS[next]} boxSize="3.5" />}
          isLoading={busy}
          onClick={() => run(next)}
        >
          {t(ACTION_LABEL_KEYS[next])}
        </Button>
        <Box w="1px" bg="purple.100" my="1" />
        <MenuButton
          as={IconButton}
          variant="ghost"
          colorScheme="purple"
          size="sm"
          borderRadius="0"
          aria-label={t("Dev shortcuts") ?? undefined}
          icon={<Icon as={FiChevronDown} />}
          isDisabled={busy}
        />
      </Flex>
      <MenuList minW="64" py="1.5" px="1.5">
        <Text px="2.5" pb="1.5" fontFamily="mono" fontSize="2xs" fontWeight="bold" letterSpacing="widest" color="smMuted" textTransform="uppercase">
          {t("Dev shortcuts")}
        </Text>
        {steps.map((step) => (
          <RoadmapRow key={step.key} step={step} onRun={() => run(step.key)} />
        ))}
      </MenuList>
    </Menu>
  );
};

// Blocked-reason keys are looked up dynamically via t(step.reasonKey); list them so langen extracts them.
// t('requires publishing first') t('requires invitation to tender first') t('requires award and dispatched letters first')
const RoadmapRow = ({ step, onRun }: { step: RoadmapStep; onRun: () => void }) => {
  const { t } = useTranslation();
  const label = t(ACTION_LABEL_KEYS[step.key]);

  if (step.state === "next") {
    return (
      <Flex
        as="button"
        onClick={onRun}
        w="full"
        align="center"
        gap="2.5"
        px="2.5"
        py="2"
        borderRadius="md"
        bg="purple.50"
        color="purple.700"
        fontWeight="semibold"
        fontSize="sm"
        _hover={{ bg: "purple.100" }}
      >
        <Icon as={ACTION_ICONS[step.key]} boxSize="4" color="purple.600" />
        <Text>{label}</Text>
        <Box
          ml="auto"
          fontSize="2xs"
          fontWeight="bold"
          color="white"
          bg="purple.600"
          px="1.5"
          py="0.5"
          borderRadius="full"
          letterSpacing="wide"
        >
          {t("Next")}
        </Box>
      </Flex>
    );
  }

  const done = step.state === "done";
  return (
    <Flex align="center" gap="2.5" px="2.5" py="2" fontSize="sm" color={done ? "smMuted" : "smTextDisabled"}>
      <Icon as={done ? FiCheckCircle : FiCircle} boxSize="4" color={done ? "smSuccess" : "smTextDisabled"} />
      <Box>
        <Text>{label}</Text>
        {done ? (
          <Text fontSize="2xs" color="smMuted">
            {step.key === "FORCE_PUBLISH" ? t("Published") : t("Passed")}
          </Text>
        ) : step.reasonKey ? (
          <Text fontSize="2xs" color="smTextDisabled">
            {t(step.reasonKey)}
          </Text>
        ) : null}
      </Box>
    </Flex>
  );
};

const formatSkew = (seconds: number) => {
  const days = Math.round(seconds / 86400);
  if (days >= 1) return `${days}d`;
  const hours = Math.round(seconds / 3600);
  if (hours >= 1) return `${hours}h`;
  return `${seconds}s`;
};
